import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { submitSucceeded, submitting } from './selectors';
import { DirtyFormProxyReactContext } from './const';
import { subscribeUnsavedFormChangesActions } from './utils';
import DirtyFormProxyConfirmation from './DirtyFormProxyConfirmation';
import { useChangeFormHandler, useBeforeunload, useChangePageHandler } from './hooks';

export const DirtyFormProxyProvider = ({ children }) => {
	const subscriber = useMemo(subscribeUnsavedFormChangesActions, []);
	const [confirmationModalOptions, setConfirmationModalOptions] = useState({
		isOpen: false,
		hasDiscard: true,
	});
	const {
		formHasChanges,
		activeDirtyFormName,
		addFormNameForObservation,
		removeFormNameForObservation,
		registerActiveDirtyFormProxyName,
		unRegisterActiveDirtyFormProxyName,
	} = useChangeFormHandler();
	const { pageHasChanges, activeDirtyPageName, setIsPageHasChanges, setActiveDirtyPageProxyName } =
		useChangePageHandler();
	const isSubmitSucceeded = useSelector(submitSucceeded(activeDirtyFormName));
	const isSubmitting = useSelector(submitting(activeDirtyFormName));

	const hasChanges = useMemo(() => {
		return (formHasChanges || pageHasChanges) && !isSubmitting;
	}, [isSubmitting, formHasChanges, pageHasChanges]);

	useBeforeunload(hasChanges && !confirmationModalOptions.isOpen);

	const registerActiveDirtyPageProxyName = useCallback(
		(name, action) => {
			setActiveDirtyPageProxyName(name);
			registerActiveDirtyFormProxyName(name);
			subscriber.registerPageAction(name, action);
		},
		[subscriber, setActiveDirtyPageProxyName, registerActiveDirtyFormProxyName],
	);

	const unRegisterActiveDirtyPageProxyName = useCallback(
		(name) => {
			setActiveDirtyPageProxyName('');
			unRegisterActiveDirtyFormProxyName(name);
			subscriber.unregisterPageAction(name);
			setIsPageHasChanges(false);
		},
		[subscriber, setIsPageHasChanges, setActiveDirtyPageProxyName, unRegisterActiveDirtyFormProxyName],
	);

	const memoValue = useMemo(
		() => ({
			subscriber,
			hasChanges,
			activeDirtyFormName,
			activeDirtyPageName,
			setIsPageHasChanges,
			setConfirmationModalOptions,
			addFormNameForObservation,
			removeFormNameForObservation,
			registerActiveDirtyFormProxyName,
			unRegisterActiveDirtyFormProxyName,
			registerActiveDirtyPageProxyName,
			unRegisterActiveDirtyPageProxyName,
		}),
		[
			subscriber,
			hasChanges,
			activeDirtyFormName,
			activeDirtyPageName,
			setIsPageHasChanges,
			addFormNameForObservation,
			removeFormNameForObservation,
			registerActiveDirtyFormProxyName,
			unRegisterActiveDirtyFormProxyName,
			registerActiveDirtyPageProxyName,
			unRegisterActiveDirtyPageProxyName,
		],
	);

	if (process.env.NODE_ENV === 'test') {
		global.__DIRTYFORMPROXYPROVIDER_CONTEXT__ = memoValue;
	}

	return (
		<DirtyFormProxyReactContext.Provider value={memoValue}>
			{children}
			<DirtyFormProxyConfirmation
				isOpen={confirmationModalOptions.isOpen}
				onDiscard={confirmationModalOptions.onDiscard}
				onClose={confirmationModalOptions.onClose}
				isModalForm={confirmationModalOptions.isModalForm}
				hideConfirmButton={confirmationModalOptions.hasDiscard}
				isSubmitSucceeded={isSubmitSucceeded}
			/>
		</DirtyFormProxyReactContext.Provider>
	);
};

export default DirtyFormProxyProvider;
